import api from '@/lib/api'
import type { DataSource } from '@/types'
import type {
  FilePreviewResponse,
  MappingSuggestionResponse,
  ValidateMappingRequest,
  ValidationResponse,
  ColumnInfo,
  SuggestedMapping,
  RequiredField,
} from '@/components/upload-wizard/types'
import { type DataSourceId, unbrand } from '@/types/branded'
import type { APIErrorResponse } from '@/types/errors'
import { extractAPIError } from '@/lib/error-utils'

/**
 * Typed result wrapper for service operations.
 * Enables discriminated union pattern for error handling.
 */
export type Result<T, E = APIErrorResponse> =
  | { success: true; data: T }
  | { success: false; error: E }

// Connector info from backend API
export interface ConnectorInfo {
  name: string
  display_name: string
  category: string
  auth_type: string
  data_entities: string[]
}

export const dataSourcesService = {
  async listConnectors() {
    const { data } = await api.get<ConnectorInfo[]>('/data-sources/connectors')
    return data
  },
  /**
   * List all data sources.
   */
  async list(): Promise<Result<{ data_sources: DataSource[]; total: number }>> {
    try {
      const { data } = await api.get<{ data_sources: DataSource[]; total: number }>('/data-sources')
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Get a single data source by ID.
   */
  async get(id: DataSourceId): Promise<Result<DataSource>> {
    try {
      const { data } = await api.get<DataSource>(`/data-sources/${unbrand(id)}`)
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  async create(dataSource: {
    connector_name: string
    display_name: string
    credentials?: Record<string, unknown>
    use_case?: string
  }) {
    const { data } = await api.post<DataSource & {
      validation?: {
        is_valid: boolean
        errors: string[]
        warnings: string[]
        stats: Record<string, unknown>
      }
    }>('/data-sources', dataSource)
    return data
  },

  /**
   * Update a data source.
   */
  async update(id: DataSourceId, dataSource: Partial<DataSource>): Promise<Result<DataSource>> {
    try {
      const { data } = await api.patch<DataSource>(`/data-sources/${unbrand(id)}`, dataSource)
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Delete a data source.
   */
  async delete(id: DataSourceId): Promise<Result<void>> {
    try {
      await api.delete(`/data-sources/${unbrand(id)}`)
      return { success: true, data: undefined }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Test connection to a data source.
   */
  async testConnection(id: DataSourceId): Promise<Result<{ status: string }>> {
    try {
      const { data } = await api.post<{ status: string }>(`/data-sources/${unbrand(id)}/test`)
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Trigger data sync for a data source.
   */
  async sync(id: DataSourceId, fullSync: boolean = false): Promise<Result<{
    message: string
    data_source_id: string
    sync_job_id: string
  }>> {
    try {
      const { data } = await api.post<{
        message: string
        data_source_id: string
        sync_job_id: string
      }>(`/data-sources/${unbrand(id)}/sync`, null, {
        params: { full_sync: fullSync },
      })
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Validate data source for a use case.
   */
  async validate(id: DataSourceId, useCase: string): Promise<Result<{
    is_valid: boolean
    errors: string[]
    warnings: string[]
    stats: Record<string, unknown>
  }>> {
    try {
      const { data } = await api.post<{
        is_valid: boolean
        errors: string[]
        warnings: string[]
        stats: Record<string, unknown>
      }>(`/data-sources/${unbrand(id)}/validate`, { use_case: useCase })
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Preview uploaded file contents
   * Note: This endpoint may not exist yet - placeholder for future implementation
   */
  async preview(fileContent: string, fileType: string): Promise<FilePreviewResponse> {
    try {
      const { data } = await api.post<FilePreviewResponse>('/data-sources/preview', {
        file_content: fileContent,
        file_type: fileType,
      })
      return data
    } catch (error) {
      // Fallback: parse file locally if endpoint doesn't exist
      return this.parseFileLocally(fileContent, fileType)
    }
  },

  /**
   * Get AI-suggested column mappings for a use case
   * Note: This endpoint may not exist yet - placeholder for future implementation
   */
  async suggestMappings(
    useCase: string,
    columns: ColumnInfo[]
  ): Promise<MappingSuggestionResponse> {
    try {
      const { data } = await api.post<MappingSuggestionResponse>(
        '/data-sources/suggest-mappings',
        {
          use_case: useCase,
          columns,
        }
      )
      return data
    } catch (error) {
      // Fallback: return basic rule-based suggestions
      return await this.getBasicMappingSuggestions(useCase, columns)
    }
  },

  /**
   * Validate column mappings before creation
   */
  async validateMapping(request: ValidateMappingRequest): Promise<ValidationResponse> {
    try {
      const { data } = await api.post<ValidationResponse>(
        '/data-sources/validate-mapping',
        request
      )
      return data
    } catch (error: any) {
      // If the backend returned validation errors, use those
      if (error?.response?.data?.errors) {
        return {
          is_valid: false,
          errors: error.response.data.errors,
          warnings: error.response.data.warnings || [],
          stats: error.response.data.stats || {},
        }
      }
      // Fallback: basic client-side validation only if backend is unavailable
      console.warn('Validation endpoint failed, using local fallback:', error?.message)
      return await this.validateMappingLocally(request)
    }
  },

  /**
   * Local fallback for file preview
   */
  parseFileLocally(fileContent: string, fileType: string): FilePreviewResponse {
    // Basic CSV parsing - simplified version
    const decoded = atob(fileContent)
    const lines = decoded.split('\n').filter((line) => line.trim())

    if (lines.length === 0) {
      throw new Error('Empty file')
    }

    const headers = lines[0].split(',').map((h) => h.trim())
    const rows = lines
      .slice(1, Math.min(6, lines.length))
      .map((line) => {
        const values = line.split(',')
        const row: Record<string, any> = {}
        headers.forEach((header, i) => {
          row[header] = values[i]?.trim() || null
        })
        return row
      })

    const columns: ColumnInfo[] = headers.map((name) => ({
      name,
      data_type: 'unknown' as const,
      null_count: 0,
      null_percentage: 0,
      sample_values: rows.map((row) => row[name]).slice(0, 3),
    }))

    return {
      columns,
      sample_rows: rows,
      total_rows: lines.length - 1,
      file_type: fileType,
    }
  },

  /**
   * Basic rule-based mapping suggestions
   */
  async getBasicMappingSuggestions(
    useCase: string,
    columns: ColumnInfo[]
  ): Promise<MappingSuggestionResponse> {
    const useCaseFields = await this.getUseCaseFields(useCase)

    const suggested_mappings = useCaseFields.required_fields
      .map((field) => {
        // Simple name matching
        const match = columns.find(
          (col) =>
            col.name.toLowerCase().includes(field.name.toLowerCase()) ||
            field.name.toLowerCase().includes(col.name.toLowerCase())
        )

        if (match) {
          return {
            target_field: field.name,
            source_column: match.name,
            confidence: 0.7,
            reason: 'Name similarity match',
          }
        }
        return null
      })
      .filter((m): m is SuggestedMapping => m !== null)

    return {
      suggested_mappings,
      required_fields: useCaseFields.required_fields as RequiredField[],
    }
  },

  /**
   * Local validation fallback
   */
  async validateMappingLocally(request: ValidateMappingRequest): Promise<ValidationResponse> {
    const { use_case, column_mappings } = request
    const fields = await this.getUseCaseFields(use_case)
    const errors: string[] = []
    const warnings: string[] = []

    // Check required fields
    const missing = fields.required_fields
      .filter((f) => f.required && !column_mappings[f.name])
      .map((f) => f.name)

    if (missing.length > 0) {
      errors.push(`Missing required fields: ${missing.join(', ')}`)
    }

    return {
      is_valid: errors.length === 0,
      errors,
      warnings,
      stats: {
        row_count: 0,
        column_count: Object.keys(column_mappings).length,
        missing_required_fields: missing,
      },
    }
  },

  /**
   * Get required fields for a use case from backend API
   * Uses the useCasesService for fetching field information
   */
  async getUseCaseFields(useCase: string): Promise<{
    required_fields: Array<{
      name: string
      description: string
      required: boolean
      data_type: string
    }>
    optional_fields?: Array<{
      name: string
      description: string
      required: boolean
      data_type: string
    }>
  }> {
    try {
      // Import useCasesService to avoid circular dependency
      const { getUseCaseFields } = await import('./useCases')
      const fields = await getUseCaseFields(useCase)
      return {
        required_fields: fields.required_fields,
        optional_fields: fields.optional_fields,
      }
    } catch (error) {
      console.warn(`Failed to fetch fields for use case ${useCase}:`, error)
      // Return empty fields as fallback
      return { required_fields: [] }
    }
  },

  /**
   * Detect data structure (transaction vs entity-level) from temp file path
   */
  async detectDataStructure(
    tempPath: string,
    useCase: string,
    entityIdColumn?: string
  ): Promise<Result<DetectionResult>> {
    try {
      const { data } = await api.post<DetectionResult>('/data-sources/detect-structure', {
        temp_path: tempPath,
        use_case: useCase,
        entity_id_column: entityIdColumn,
      })
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Detect data structure from an existing data source (downloads from R2)
   * Use this when file is already uploaded and you have the data source ID
   */
  async detectDataStructureByDataSource(
    id: DataSourceId,
    useCase: string = 'ecommerce_churn',
    entityIdColumn?: string
  ): Promise<Result<DetectionResult>> {
    try {
      const { data } = await api.post<DetectionResult>('/data-sources/detect-structure-by-datasource', {
        data_source_id: unbrand(id),
        use_case: useCase,
        entity_id_column: entityIdColumn,
      })
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Trigger data aggregation for transaction-level data
   */
  async triggerAggregation(
    id: DataSourceId,
    useCase: string,
    aggregationConfig: AggregationConfig
  ): Promise<Result<AggregateResponse>> {
    try {
      const { data } = await api.post<AggregateResponse>('/data-sources/aggregate', {
        data_source_id: unbrand(id),
        use_case: useCase,
        aggregation_config: aggregationConfig,
      })
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Get aggregation status for a data source
   */
  async getAggregationStatus(id: DataSourceId): Promise<Result<AggregationStatusResponse>> {
    try {
      const { data } = await api.get<AggregationStatusResponse>(
        `/data-sources/${unbrand(id)}/aggregation-status`
      )
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Get columns from a data source preview.
   */
  async getColumns(id: DataSourceId): Promise<Result<string[]>> {
    try {
      const { data } = await api.get<{ columns: string[]; rows: any[]; total_count: number }>(
        `/data-sources/${unbrand(id)}/preview?limit=1`
      )
      return { success: true, data: data.columns }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Validate if a data source is compatible with a specific use case
   */
  async validateForUseCase(
    id: DataSourceId,
    useCaseName: string
  ): Promise<Result<ValidateForUseCaseResponse>> {
    try {
      const { data } = await api.post<ValidateForUseCaseResponse>(
        '/data-sources/validate-for-use-case',
        {
          data_source_id: unbrand(id),
          use_case_name: useCaseName,
        }
      )
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Resolve user intent to the most appropriate use case using fuzzy matching
   *
   * This maps general concepts like "customer churn" to the correct domain-specific
   * use case based on the data columns (e.g., telecom_churn for telecom data)
   */
  async resolveUseCase(
    userIntent: string,
    id?: DataSourceId,
    availableColumns?: string[]
  ): Promise<Result<ResolveUseCaseResponse>> {
    try {
      const { data } = await api.post<ResolveUseCaseResponse>(
        '/data-sources/resolve-use-case',
        {
          user_intent: userIntent,
          data_source_id: id ? unbrand(id) : undefined,
          available_columns: availableColumns,
        }
      )
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Match data source columns to all available use cases
   * Returns ranked list of trainable use cases based on column compatibility
   */
  async matchColumnsToUseCases(
    id: DataSourceId
  ): Promise<Result<MatchColumnsResponse>> {
    try {
      const { data } = await api.post<MatchColumnsResponse>(
        '/data-sources/match-columns',
        { data_source_id: unbrand(id) }
      )
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },

  /**
   * Get compatible use cases for a data source
   * Returns which use cases can be trained with this data and which need column mapping
   */
  async getCompatibleUseCases(id: DataSourceId): Promise<Result<CompatibleUseCasesResponse>> {
    try {
      const { data } = await api.get<CompatibleUseCasesResponse>(
        `/data-sources/${unbrand(id)}/compatible-use-cases`
      )
      return { success: true, data }
    } catch (error) {
      const apiError = extractAPIError(error)
      return { success: false, error: apiError || { error_code: 'UNKNOWN_ERROR', message: String(error) } }
    }
  },
}

// Type definitions for fuzzy use case resolution
export interface ResolveUseCaseResponse {
  matched_use_case: string | null
  display_name: string | null
  confidence: number
  reason: string
  detected_domain: string | null
  alternatives: string[]
  is_exact_match: boolean
}

// Type definitions for data structure detection
export interface DetectionResult {
  data_type: 'transaction' | 'entity_level'
  confidence: number
  evidence: string[]
  suggested_entity_id: string | null
  suggested_timestamp: string | null
  suggested_amount: string | null
  rows_per_entity_stats: {
    min: number
    max: number
    mean: number
    median: number
  }
  requires_aggregation: boolean
}

export interface AggregationConfig {
  // Backend field names (required by /data-sources/aggregate endpoint)
  customer_id_col: string
  transaction_date_col: string
  amount_col?: string
  aggregation_functions?: Record<string, string[]>
}

export interface AggregateResponse {
  job_id: string
  status: 'queued' | 'pending' | 'running' | 'completed' | 'failed'
  message: string
}

// Matches backend AggregationStatusResponse Pydantic model
// Returns metadata about whether aggregation was applied to this data source
export interface AggregationStatusResponse {
  // Status fields for polling (pending, in_progress, completed, failed)
  status: 'pending' | 'in_progress' | 'completed' | 'failed'
  progress: number // 0-100 progress percentage
  error_message: string | null

  // Existing fields
  aggregation_applied: boolean
  original_row_count: number | null
  aggregated_row_count: number | null
  aggregated_object_key: string | null
  features_created: string[]
  aggregated_at: string | null
}

// Type definitions for column matching
export interface MatchColumnsResponse {
  data_source_id: string
  columns: string[]
  suggestions: UseCaseSuggestion[]
}

// Type definitions for use case validation
export interface UseCaseSuggestion {
  use_case_name: string
  display_name: string
  confidence: number
  is_trainable: boolean
  reason?: string
}

export interface ValidateForUseCaseResponse {
  is_compatible: boolean
  validation_result: {
    is_valid: boolean
    missing_required: string[]
    matched_required: string[]
    matched_optional: string[]
    has_target: boolean
    suggestions: string[]
  }
  requested_use_case: {
    name: string
    display_name: string
    target_column: string
  }
  alternative_suggestions: UseCaseSuggestion[]
  error_message: string | null
}

// Type definitions for compatible use cases
export interface CompatibleUseCase {
  use_case_id: string
  use_case_name: string
  display_name: string
  is_ready: boolean
  needs_mapping: boolean
  missing_columns: string[]
  available_columns: string[]
  confidence: number
}

export interface CompatibleUseCasesResponse {
  data_source_id: string
  use_cases: CompatibleUseCase[]
}
